import {
  Component,
  Show,
  createSignal,
  createMemo,
  createEffect,
} from 'solid-js';
import { getClosestNamedColor } from '@trimble/ui-components/solidjs';
import { ColorPicker, ColorPickerProps } from '@trimble/ui-components/solidjs';
import { RgbColor } from 'solid-color';
import tinycolor from 'tinycolor2';
import { CustomTooltip } from '@tcweb/connect-ui';

export interface ColorPropertyProps
  extends Omit<ColorPickerProps, 'initialColor'> {
  selectedColor: string;
  objectColor?: string | null;
  byLayerColor?: string | null;
  isByLayer: boolean;
  canEdit: boolean;
  disabled?: boolean;
  cssAlignment?: string;
  noBorder?: boolean;
  hideIcon?: boolean;
  /**
   * Row density. `default` matches compact layer-list rows; `comfortable` is taller with
   * larger icon, swatch, and label text for alignment with property-panel inputs.
   */
  density?: 'default' | 'comfortable';
  /** When `density` is `comfortable`, sets hex / color-name `font-size` (e.g. `"11px"`) to match an adjacent input. */
  comfortableTextSize?: string;
  /**
   * When `density` is `comfortable`, added to hex / color-name spans so a parent can set `font-size` (e.g. with `!important`)
   * and guarantee parity with a neighboring input.
   */
  comfortableTextClass?: string;
  /**
   * When `density` is `comfortable`, fixed row height (e.g. `"2.413rem"`) to match an adjacent input. Defaults to `2rem` (`h-8`).
   */
  comfortableRowHeight?: string;
  onColorChange?(color: string | null): void;
  onByLayerSelect?(): void;
}

const VARIES_LABEL = '*varies*';

export const ColorProperty: Component<ColorPropertyProps> = (props) => {
  const density = createMemo(() => props.density ?? 'default');
  const isComfortable = createMemo(() => density() === 'comfortable');

  const comfortableLabelStyle = createMemo(
    (): { fontSize?: string } | undefined => {
      if (
        !isComfortable() ||
        props.comfortableTextClass ||
        !props.comfortableTextSize
      ) {
        return undefined;
      }
      return { fontSize: props.comfortableTextSize };
    }
  );

  const comfortableTextExtraClass = createMemo(() =>
    isComfortable() && props.comfortableTextClass
      ? props.comfortableTextClass
      : ''
  );

  const isComfortableTallRow = createMemo(
    () => isComfortable() && !!props.comfortableRowHeight
  );

  const comfortableRowStyle = createMemo(
    ():
      | {
          height: string;
          minHeight: string;
          maxHeight: string;
          boxSizing: 'border-box';
        }
      | undefined => {
      if (!isComfortable() || !props.comfortableRowHeight) {
        return undefined;
      }
      const h = props.comfortableRowHeight;
      return {
        height: h,
        minHeight: h,
        maxHeight: h,
        boxSizing: 'border-box',
      };
    }
  );

  const [showPicker, setShowPicker] = createSignal(false);
  const [byLayerColorSelected, setByLayerColorSelected] = createSignal(false);
  const [currentColor, setCurrentColor] = createSignal<string>('');
  const [layerColor, setLayerColor] = createSignal<string>('');

  // Memo to determine if the color picker is editable (canEdit and not disabled)
  const isEditable = createMemo(() => props.canEdit && !props.disabled);

  createEffect(() => {
    setCurrentColor(props.selectedColor);
  });

  createEffect(() => {
    if (props.byLayerColor) {
      setLayerColor(props.byLayerColor);
    }
  });

  createEffect(() => {
    if (
      props.isByLayer &&
      (props.objectColor === undefined ||
        props.objectColor === null ||
        props.objectColor === '')
    ) {
      setByLayerColorSelected(true);
    } else {
      setByLayerColorSelected(false);
    }
  });

  // Determine the current display color
  const currentDisplayColor = createMemo(() => {
    return byLayerColorSelected() ? layerColor() : currentColor();
  });

  // Determine display name and whether it has multiple words
  const displayName = createMemo(() =>
    getClosestNamedColor(currentDisplayColor())
  );

  // Create tooltip text with color name and hex code
  const tooltipText = createMemo(() => {
    if (currentDisplayColor() === VARIES_LABEL) {
      return VARIES_LABEL;
    }
    const name =
      props.isByLayer && byLayerColorSelected() ? 'ByLayer' : displayName();
    const hex = currentDisplayColor();
    return `${name} ${hex}`;
  });

  // Ensure proper hex format for ColorPicker
  const getInitialColorForPicker = createMemo(() => {
    const color = currentColor();
    // Ensure it starts with # if it's a hex color
    if (color && !color.startsWith('#')) {
      return `#${color}`;
    }
    return color;
  });

  const handleColorSave = (rgb: RgbColor): void => {
    const colorHex = tinycolor(rgb).toHexString();
    setCurrentColor(colorHex);
    setByLayerColorSelected(false);
    props.onColorChange?.(colorHex);
    setShowPicker(false);
  };

  const handleByLayerSelect = (): void => {
    setCurrentColor(props.byLayerColor!);
    props.onByLayerSelect?.();
    setByLayerColorSelected(true);
    setShowPicker(false);
  };

  return (
    <div class="flex flex-row items-center min-w-0 w-full gap-x-4">
      <Show when={!props.hideIcon}>
        <modus-wc-icon
          size={isComfortableTallRow() ? 'lg' : isComfortable() ? 'md' : 'sm'}
          class={`my-auto flex-shrink-0 ${isComfortableTallRow() ? 'h-[26px]' : isComfortable() ? 'h-[22px]' : 'h-[20px]'}`}
          name="palette"
        />
      </Show>
      <div class="relative min-w-0 flex-1">
        <CustomTooltip
          class="w-full block"
          text={tooltipText()}
          position="top-start"
        >
          <div
            class={`flex items-center rounded-[4px] pr-2.5 w-full overflow-hidden ${isComfortable() && !props.comfortableRowHeight ? 'h-8' : !isComfortable() ? 'h-7' : ''} ${props.noBorder ? '' : 'border-[1px] border-solid border-gray-6'} ${
              isEditable()
                ? 'cursor-pointer hover:border-gray-8 hover:bg-gray-1'
                : props.disabled
                  ? 'pl-2.5 opacity-50 cursor-not-allowed'
                  : ''
            }`}
            style={comfortableRowStyle()}
            onClick={() =>
              isEditable() ? setShowPicker(!showPicker()) : undefined
            }
          >
            <Show when={currentDisplayColor() !== VARIES_LABEL}>
              <span
                data-testid="color-swatch"
                class={`rounded-full flex-shrink-0 ${isComfortableTallRow() ? 'w-5 h-5' : isComfortable() ? 'w-[18px] h-[18px]' : 'w-[16px] h-[16px]'}`}
                style={{
                  ['background-color']: currentDisplayColor(),
                }}
              />
            </Show>
            <div class="min-w-0 flex-1 flex items-center overflow-hidden">
              <Show
                when={currentDisplayColor() !== VARIES_LABEL}
                fallback={
                  <span
                    class={`truncate min-w-0 font-medium ${currentDisplayColor() === VARIES_LABEL ? '' : 'ml-2'} ${comfortableTextExtraClass()} ${isComfortable() && !props.comfortableTextSize && !props.comfortableTextClass ? 'text-sm' : !isComfortable() ? 'text-xs' : ''}`}
                    style={comfortableLabelStyle()}
                  >
                    {props.isByLayer && byLayerColorSelected()
                      ? 'ByLayer'
                      : displayName()}
                  </span>
                }
              >
                <span
                  class={`text-gray-6 ml-2 flex-shrink-0 whitespace-nowrap ${comfortableTextExtraClass()} ${isComfortable() && !props.comfortableTextSize && !props.comfortableTextClass ? 'text-sm' : !isComfortable() ? 'text-xs' : ''}`}
                  style={comfortableLabelStyle()}
                >
                  {currentDisplayColor()}
                </span>
              </Show>
            </div>
            <Show when={isEditable()}>
              <modus-wc-icon
                name="expand_more"
                size={
                  isComfortableTallRow() ? 'lg' : isComfortable() ? 'md' : 'sm'
                }
                class={`ml-2 flex-shrink-0 ${isComfortableTallRow() ? 'w-6 h-6' : isComfortable() ? 'w-5 h-5' : 'w-4 h-4'}`}
              />
            </Show>
          </div>
        </CustomTooltip>

        <Show when={showPicker() && isEditable()}>
          <div
            class={`absolute top-full z-50 ${props.cssAlignment && props.cssAlignment !== '' ? props.cssAlignment : 'right-0'}`}
          >
            <ColorPicker
              initialColor={getInitialColorForPicker()}
              onSave={handleColorSave}
              onByLayerSelect={handleByLayerSelect}
              onClose={() => setShowPicker(false)}
              useTransparency={props.useTransparency}
              initialTransparency={props.initialTransparency}
              onTransparencyChange={props.onTransparencyChange}
              byLayerColor={props.byLayerColor}
              objectColor={
                currentDisplayColor() === VARIES_LABEL ||
                props.objectColor === null
                  ? null
                  : currentDisplayColor()
              }
              cssAlignment={props.cssAlignment}
              suppressSelection={currentDisplayColor() === VARIES_LABEL}
            />
          </div>
        </Show>
      </div>
    </div>
  );
};

export default ColorProperty;
